import { sanitizeColumns } from './columnOps';
import type { FieldKey, TableView, ViewColumn, ViewContent, ViewSort } from './types';

/** More rows than any page offers; a bigger stored value is not something the table asked for. */
const MAX_PAGE_SIZE = 500;

/**
 * Turns whatever storage (or later, the API) hands back into a view the table can render, or
 * `undefined` when there is nothing recognisable to keep. Columns are reconciled with this build's
 * catalogue; a missing sort falls back to the table's default.
 *
 * A stored field the catalogue no longer has (the backend stopped sending it, a release removed it)
 * is dropped here, at load: the view renders without it, and the next save writes the cleaned list,
 * so it goes away from storage without anyone having to notice it.
 */
export const sanitizeView = (
  raw: unknown,
  ctx: { tableKey: string; knownKeys: FieldKey[]; defaultColumns: () => ViewColumn[]; defaultSort: ViewSort },
): TableView | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Partial<TableView>;
  if (typeof source.id !== 'string' || !source.id || source.tableKey !== ctx.tableKey) return undefined;

  const sort =
    source.sort && typeof source.sort.sortField === 'string' && (source.sort.sortDirection === 'ASC' || source.sort.sortDirection === 'DESC')
      ? { sortField: source.sort.sortField, sortDirection: source.sort.sortDirection }
      : { ...ctx.defaultSort };
  const filter = source.filter && typeof source.filter === 'object' && !Array.isArray(source.filter) ? { ...source.filter } : {};
  const favouriteFilters = Array.isArray(source.favouriteFilters) ? source.favouriteFilters.filter(key => typeof key === 'string') : undefined;
  const pagingMode = source.pagingMode === 'scroll' || source.pagingMode === 'pages' ? source.pagingMode : undefined;
  const pageSize = Number.isInteger(source.pageSize) && source.pageSize > 0 && source.pageSize <= MAX_PAGE_SIZE ? source.pageSize : undefined;
  const now = new Date().toISOString();

  return {
    id: source.id,
    tableKey: ctx.tableKey,
    name: typeof source.name === 'string' && source.name.trim() ? source.name : source.id,
    ownerId: typeof source.ownerId === 'string' ? source.ownerId : '',
    ...(typeof source.ownerFullName === 'string' ? { ownerFullName: source.ownerFullName } : {}),
    ...(typeof source.editable === 'boolean' ? { editable: source.editable } : {}),
    visibility: source.visibility === 'COMPANY' ? 'COMPANY' : 'PRIVATE',
    filter,
    sort,
    columns: sanitizeColumns(source.columns, ctx.knownKeys, ctx.defaultColumns),
    ...(favouriteFilters ? { favouriteFilters } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(pagingMode ? { pagingMode } : {}),
    createdAt: typeof source.createdAt === 'string' ? source.createdAt : now,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : now,
    version: typeof source.version === 'number' && Number.isFinite(source.version) ? source.version : 1,
  };
};

/**
 * The same reconciliation for an unsaved working copy, which can outlive a release just like a saved
 * view: a field the catalogue no longer has is dropped here too, so a draft never brings it back.
 */
export const sanitizeDraft = (raw: unknown, ctx: { knownKeys: FieldKey[]; defaultColumns: () => ViewColumn[]; defaultSort: ViewSort }): ViewContent | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Partial<ViewContent>;
  const view = sanitizeView({ ...source, id: 'draft', tableKey: 'draft' }, { ...ctx, tableKey: 'draft' });
  if (!view) return undefined;
  const { filter, sort, columns, favouriteFilters, pageSize, pagingMode } = view;
  return { filter, sort, columns, ...(favouriteFilters ? { favouriteFilters } : {}), ...(pageSize ? { pageSize } : {}), ...(pagingMode ? { pagingMode } : {}) };
};
